// Central content for Vigyaved Healthcare + NICE

export const IMG = {
  heroNeural: "https://static.prod-images.emergentagent.com/jobs/bcc8ed47-9b0f-4ac3-8692-c362b4969cd7/images/6266aa6f831eb6fee054219345da9be8ee97e990ba0bf4ce11a50edace52e2b5.jpeg",
  athena: "https://static.prod-images.emergentagent.com/jobs/bcc8ed47-9b0f-4ac3-8692-c362b4969cd7/images/5886596a056e591036491be070fba8e54feee45782ac5db74ee11d406aa394cf.jpeg",
  digitalTwin: "https://static.prod-images.emergentagent.com/jobs/bcc8ed47-9b0f-4ac3-8692-c362b4969cd7/images/bf24f21678b4d9024c72644efc640e6f0f350c38027f650f6412738ab244d504.jpeg",
  exposome: "https://static.prod-images.emergentagent.com/jobs/bcc8ed47-9b0f-4ac3-8692-c362b4969cd7/images/74eace312a1289521763164923ab9be0de76253b76f04b943cb09088abb5ba8b.jpeg",
  dna: "https://images.unsplash.com/photo-1643780668909-580822430155?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  hospital: "https://images.unsplash.com/photo-1587351021821-f871837248c6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  lab: "https://images.unsplash.com/photo-1582560475093-ba66accbc424?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  nice_hero: "https://static.prod-images.emergentagent.com/jobs/bcc8ed47-9b0f-4ac3-8692-c362b4969cd7/images/95bd8ffb0164a08b48274cf55dcad376c580ea27b43364f6d520e70e86640ae6.jpeg",
  athena_bg: "https://static.prod-images.emergentagent.com/jobs/bcc8ed47-9b0f-4ac3-8692-c362b4969cd7/images/a6b1d2f8a748f836a159ffe21639e91edfeefb6dcb87b1f3d286f156a5e30bc6.jpeg",
};

export const VIGYAVED_MODEL = [
  "Science", "Research", "Data", "Intelligence", "Clinical Innovation", "Healthcare", "Better Healthspan",
];

export const WHAT_WE_DO = [
  { k: "01", t: "Healthcare", d: "Integrated clinical and preventive healthcare across the life course." },
  { k: "02", t: "Research", d: "Clinical, translational and population-health research." },
  { k: "03", t: "Technology", d: "AI, digital health and computational medicine." },
  { k: "04", t: "Innovation", d: "New tools, platforms and healthcare delivery models." },
  { k: "05", t: "Education", d: "Training clinicians, researchers and healthcare innovators." },
];

export const MARQUEE_TERMS = [
  "APEX-T2D", "RISE-30", "ATHENA™", "Exposome Science", "Pollution–Health", "Computational Cardiology",
  "Human Digital Twins", "Precision Health", "Preventive Cardiology", "Healthspan",
];

// Vigyaved-level top nav
export const NAV = [
  { label: "About", to: "/about" },
  { label: "Healthcare", to: "/healthcare" },
  { label: "Research", to: "/research" },
  { label: "Innovation", to: "/innovation" },
  { label: "NICE", to: "/nice" },
  { label: "Publications", to: "/publications" },
  { label: "Contact", to: "/contact" },
];

// NICE sub-nav
export const NICE_NAV = [
  { label: "About NICE", to: "/nice/about" },
  { label: "Cardiometabolic", to: "/nice/cardiometabolic" },
  { label: "Precision Health", to: "/nice/precision-health" },
  { label: "AI & Computational", to: "/nice/ai" },
  { label: "ATHENA", to: "/nice/athena" },
  { label: "WebApps", to: "/nice/webapps" },
  { label: "Environmental", to: "/nice/environmental" },
  { label: "Aging & Healthspan", to: "/nice/aging" },
  { label: "Research Programs", to: "/nice/research-programs" },
  { label: "Clinical Research", to: "/nice/clinical-research" },
  { label: "Education", to: "/nice/education" },
  { label: "Network", to: "/nice/network" },
];

// Data-driven pages consumed by DomainPage
export const PAGES = {
  "about": {
    eyebrow: "About Vigyaved",
    title: "An ecosystem for intelligent, evidence-based healthcare.",
    lead: "Vigyaved Healthcare connects clinical medicine, biomedical research, digital health, artificial intelligence and preventive health — all held to a single standard: the strength of evidence.",
    image: IMG.dna,
    story: {
      kicker: "VIGYA · VED",
      title: "Science met with the wisdom of the Vedas.",
      body: [
        "The name Vigyaved joins Vigyan — science — with Veda — knowledge. It honours the ancient principles of care, prevention and balance that have guided healing for millennia, and treats them as a foundation worth building upon.",
        "But tradition alone is not enough. Vigyaved seeks the additional strength of modern evidence — rigorously testing, measuring and validating what works — so that timeless principles of care are carried forward with the confidence of evidence-based medicine.",
      ],
    },
    sections: [
      { h: "Our Vision", p: "To build a healthcare ecosystem in which disease is identified earlier, risk is quantified more precisely, treatment is individualized, and health is managed longitudinally rather than episodically." },
      { h: "Our Mission", p: "Vigyaved integrates medicine, science, technology and data into practical systems capable of improving prevention, diagnosis, treatment and long-term health." },
      { h: "Grounded in Evidence", p: "Every model, program and recommendation is anchored in evidence-based medicine — transparent about the quality of data behind it, and continuously validated against real-world outcomes." },
      { h: "Longitudinal by Design", p: "Health is a lifelong trajectory. We build systems that learn continuously, so care improves with every data point rather than resetting at each visit." },
    ],
    model: true,
    leadership: true,
  },
  "healthcare": {
    eyebrow: "Healthcare",
    title: "Integrated, preventive, digital.",
    lead: "Care that is continuous rather than episodic — combining clinical medicine, prevention, and digital tools to manage health across the life course.",
    image: IMG.hospital,
    cards: [
      { t: "Integrated Care", d: "Coordinated management across cardiometabolic, renal and metabolic systems." },
      { t: "Preventive Health", d: "Identifying and intervening on risk before clinical events occur." },
      { t: "Digital Health", d: "Longitudinal monitoring, engagement and remote data capture." },
      { t: "Telemedicine", d: "Accessible specialist care delivered beyond the hospital walls." },
    ],
  },
  "research": {
    eyebrow: "Research & Innovation",
    title: "From question to clinical impact.",
    lead: "Clinical, translational and population-health research that moves discovery into practice — grounded in rigorous, evidence-based medicine.",
    image: IMG.lab,
    cards: [
      { t: "Clinical Research", d: "Prospective cohorts, trials, registries and real-world evidence." },
      { t: "Translational Research", d: "Bridging laboratory science and clinical application." },
      { t: "AI & Computational Medicine", d: "Turning longitudinal data into clinical intelligence." },
      { t: "Health Technology", d: "Development and validation of new tools and platforms." },
    ],
  },
  "innovation": {
    eyebrow: "Technology & Innovation",
    title: "The Vigyaved innovation portfolio.",
    lead: "Vigyaved builds the platforms; NICE provides the scientific and clinical application.",
    image: IMG.athena,
    cards: [
      { t: "ATHENA™", d: "AI-driven healthspan and trajectory intelligence." },
      { t: "Human Digital Twins", d: "Computational models of individual health trajectories." },
      { t: "Hospital Intelligence Systems", d: "AI-enabled clinical and operational intelligence." },
      { t: "Clinical Decision Support", d: "Risk prediction and personalized clinical recommendations." },
      { t: "Digital Health Platforms", d: "Longitudinal patient monitoring and engagement." },
    ],
  },
  "publications": {
    eyebrow: "Publications & Scientific Output",
    title: "Research, publications, projects, investigators.",
    lead: "A searchable architecture establishing NICE as a research institution — filterable across domains as output grows.",
    image: IMG.lab,
    publications: true,
  },
  "collaborations": {
    eyebrow: "Collaborations",
    title: "A distributed network for cardiometabolic science.",
    lead: "NICE operates as a collaborative network connecting clinicians, researchers, hospitals, academia, data scientists, technology partners and public-health organizations.",
    image: IMG.hospital,
    cards: [
      { t: "Academic Institutions", d: "Joint research programs and shared scientific infrastructure." },
      { t: "Hospitals & Health Systems", d: "Multicentre studies and implementation research." },
      { t: "Technology Partners", d: "Co-development of AI and digital-health platforms." },
      { t: "Public-Health Organizations", d: "Population-scale prevention and exposome research." },
    ],
  },
  // NICE pages
  "nice-about": {
    eyebrow: "About NICE",
    title: "Why NICE?",
    lead: "Cardiovascular disease, diabetes, obesity, kidney disease, inflammation, environmental exposures and aging interact throughout the life course. Traditional healthcare addresses these in separate silos. NICE develops an alternative — integrated, evidence-based and longitudinal: Integrated → Predictive → Preventive → Personalized → Longitudinal.",
    image: IMG.nice_hero,
    chips: ["Integrated", "Predictive", "Preventive", "Personalized", "Longitudinal"],
    cards: [
      { t: "Integrated Cardiometabolic Care", d: "One connected biological system, not isolated diseases." },
      { t: "Precision Health", d: "From population averages to individual trajectories." },
      { t: "Research & Translational Science", d: "Evidence generated and moved into practice." },
      { t: "AI & Digital Health", d: "Computational intelligence for prediction and monitoring." },
      { t: "Prevention & Healthspan", d: "Extending healthy years, not just lifespan." },
    ],
  },
  "nice-cardiometabolic": {
    eyebrow: "Cardiometabolic Medicine",
    title: "Integrated cardiometabolic health.",
    lead: "Move from disease-specific management toward integrated, evidence-based cardiometabolic risk management.",
    image: IMG.nice_hero,
    chips: ["Cardiovascular disease","Diabetes","Obesity","Hypertension","Dyslipidaemia","Heart failure","Chronic kidney disease","ASCVD","Preventive cardiology"],
    cards: [
      { t: "Cardiovascular Medicine", d: "Prevention, ASCVD, heart failure, lipidology, hypertension and residual risk." },
      { t: "Diabetes & Metabolic Medicine", d: "Diabetes prevention, complications and cardiometabolic therapeutics." },
      { t: "Obesity & Nutrition", d: "Obesity as a systemic cardiometabolic disease." },
      { t: "Cardio-Renal-Metabolic Health", d: "Integrated management of cardiovascular, renal and metabolic disease." },
      { t: "Preventive Cardiology", d: "Risk identification and intervention before events." },
    ],
  },
  "nice-precision-health": {
    eyebrow: "Precision Health",
    title: "From population averages to individual trajectories.",
    lead: "Integrating clinical data, biomarkers, genomics, environment, behaviour, longitudinal trajectories and real-world data to develop individualized risk profiles.",
    image: IMG.dna,
    cards: [
      { t: "Precision Medicine", d: "Individualized diagnosis and therapeutic strategy." },
      { t: "Biomarker Science", d: "Molecular and physiological signals of risk and response." },
      { t: "Genomics", d: "Genetic determinants of cardiometabolic disease." },
      { t: "Risk Prediction", d: "Quantifying risk trajectories over the life course." },
      { t: "Digital Phenotyping", d: "Continuous behavioural and physiological signals." },
      { t: "Real-World Data", d: "Evidence from routine clinical and population data." },
    ],
  },
  "nice-environmental": {
    eyebrow: "Environmental & Exposome Health",
    title: "Health does not occur in isolation from the environment.",
    lead: "A dedicated environmental cardiometabolic program — the institutional home for pollution–health analytics, environmental cardiology and exposome research.",
    image: IMG.exposome,
    cards: [
      { t: "Pollution & Cardiovascular Health", d: "Relationships between environmental exposures and cardiovascular disease." },
      { t: "Environmental Cardiology", d: "Environmental determinants of cardiovascular risk." },
      { t: "Exposomics", d: "Cumulative environmental exposures across the life course." },
      { t: "Climate & Health", d: "Emerging interactions between climate exposures and cardiometabolic health." },
    ],
  },
  "nice-aging": {
    eyebrow: "Aging & Healthspan",
    title: "From lifespan to healthspan.",
    lead: "NICE approaches aging as a multidimensional biological and clinical trajectory — with ATHENA modelling evidence-based aging trajectories.",
    image: IMG.digitalTwin,
    athena: true,
    cards: [
      { t: "Healthy Aging", d: "Preserving function and independence with age." },
      { t: "Biological Aging", d: "Molecular and physiological clocks of aging." },
      { t: "Cardiometabolic Aging", d: "Aging of the heart, vasculature and metabolism." },
      { t: "Healthspan Prediction", d: "Forecasting healthy years remaining." },
      { t: "Functional Decline", d: "Detecting and intervening on early decline." },
      { t: "Longevity Analytics", d: "AI-based aging trajectories via ATHENA™." },
    ],
  },
  "nice-research-programs": {
    eyebrow: "Research Programs",
    title: "Flagship programs, not buried projects.",
    lead: "Dedicated research programs advancing cardiometabolic, environmental and computational science.",
    image: IMG.lab,
    cards: [
      { t: "APEX-T2D", d: "Diabetes and cardiometabolic risk research program." },
      { t: "RISE-30", d: "A risk-prediction framework for clinically relevant trajectories." },
      { t: "Pollution–Health Analytics", d: "Quantitative environmental exposure and outcome research." },
      { t: "Exposome Science", d: "Cumulative environmental determinants of disease." },
      { t: "Computational Cardiology", d: "AI for cardiovascular phenotyping, prediction and decisions." },
      { t: "Additional Programs", d: "New NICE programs added as they mature." },
    ],
  },
  "nice-clinical-research": {
    eyebrow: "Clinical Research & Trials",
    title: "From research question to clinical impact.",
    lead: "NICE supports the full spectrum of rigorous clinical and translational research.",
    image: IMG.hospital,
    chips: ["Prospective cohorts","Observational studies","Investigator-initiated trials","Biomarker studies","Registries","Real-world evidence","Implementation research","Translational research"],
    pathway: ["Question","Protocol","Data","Analysis","Validation","Translation","Implementation"],
  },
  "nice-education": {
    eyebrow: "Education & Training",
    title: "Building the next generation of physician-scientists.",
    lead: "Courses, workshops, fellowships, webinars and CME programs across medicine, research and AI.",
    image: IMG.lab,
    chips: ["Cardiometabolic medicine","Preventive cardiology","Clinical research methodology","Biostatistics","AI in medicine","Digital health","Epidemiology","Scientific writing","Research mentorship","Translational medicine"],
  },
  "nice-network": {
    eyebrow: "NICE Network",
    title: "A distributed network for cardiometabolic science.",
    lead: "Not simply a physical institute — a collaborative network enabling multicentre research, shared expertise and scalable healthcare innovation.",
    image: IMG.hospital,
    network: ["Clinicians","Researchers","Hospitals","Academic institutions","Data scientists","Technology partners","Public-health organizations"],
  },
};

export const PUBLICATION_FILTERS = ["All","Cardiometabolic","Environmental Health","AI","Diabetes","Cardiovascular","Aging","Digital Health","Population Health"];

export const PUBLICATIONS = [
  { title: "Integrated cardiometabolic risk trajectories: a longitudinal framework", year: "2025", venue: "Working Paper", tags: ["Cardiometabolic","AI"] },
  { title: "RISE-30: a 30-year risk-prediction model for cardiovascular events", year: "2025", venue: "NICE Research Program", tags: ["Cardiovascular","AI"] },
  { title: "Ambient pollution exposure and acute coronary syndromes", year: "2024", venue: "Environmental Cardiology", tags: ["Environmental Health","Cardiovascular"] },
  { title: "ATHENA™: modelling healthspan from multidimensional health data", year: "2025", venue: "Computational Medicine", tags: ["AI","Aging"] },
  { title: "APEX-T2D: cardiometabolic phenotypes in early type 2 diabetes", year: "2024", venue: "NICE Research Program", tags: ["Diabetes","Cardiometabolic"] },
  { title: "Human Digital Twins for personalized cardiovascular prevention", year: "2025", venue: "Digital Health", tags: ["AI","Digital Health"] },
  { title: "Exposomics and residual cardiovascular risk", year: "2024", venue: "Exposome Research", tags: ["Environmental Health","Population Health"] },
  { title: "Biological aging clocks and cardiometabolic disease", year: "2023", venue: "Aging & Healthspan", tags: ["Aging","Cardiometabolic"] },
];
